import { ApiClientError, apiRequest, type ApiError, type RequestOptions } from '../api/http'
import type { AuthResponse, AuthUser, InvitationAcceptance, LoginRequest, RegisterRequest } from './types'

const AUTH_BASE = '/api/auth'

type AuthRequestOptions = Pick<RequestOptions, 'method' | 'body' | 'accessToken' | 'skipSessionRefresh'>

export class AuthApiError extends ApiClientError {
  constructor(status: number, apiError: ApiError) {
    super(status, apiError)
    this.name = 'AuthApiError'
  }
}

async function authRequest<T>(path: string, options: AuthRequestOptions = {}): Promise<T> {
  try {
    return await apiRequest<T>(AUTH_BASE, path, options)
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new AuthApiError(error.status, {
        code: error.code,
        message: error.message,
        fieldErrors: error.fieldErrors,
      })
    }
    throw error
  }
}

export function register(request: RegisterRequest): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/register', { method: 'POST', body: request })
}

export function login(request: LoginRequest): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/login', { method: 'POST', body: request })
}

export function refresh(): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/refresh', { method: 'POST', skipSessionRefresh: true })
}

export function logout(): Promise<void> {
  return authRequest<void>('/logout', { method: 'POST' })
}

export function me(accessToken: string): Promise<AuthUser> {
  return authRequest<AuthUser>('/me', { accessToken })
}

export function updateLocale(accessToken: string, locale: 'ar' | 'en'): Promise<AuthUser> {
  return authRequest<AuthUser>('/me/locale', { method: 'PATCH', accessToken, body: { locale } })
}

export function requestPasswordReset(email: string): Promise<void> {
  return authRequest<void>('/password-reset/request', { method: 'POST', body: { email } })
}

export function confirmPasswordReset(token: string, newPassword: string): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/password-reset/confirm', {
    method: 'POST',
    body: { token, password: newPassword },
  })
}

export function getInvitationAcceptance(token: string): Promise<InvitationAcceptance> {
  return authRequest<InvitationAcceptance>(`/invitations/acceptance?token=${encodeURIComponent(token)}`)
}

export function acceptInvitation(token: string, password: string, locale: 'ar' | 'en'): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/invitations/accept', {
    method: 'POST',
    body: { token, password, locale },
  })
}
